<template>
	<div>
		<!-- Submenu -->
		<users-submenu />

		<core-layout-content full>
			<core-layout-headline-and-actions>
				<template slot="headline">
					{{ $t("manage_users") }}
					<core-pagination-info
						:itemsPerPage="itemsPerPage"
						:numberOfItems="listing.numberOfItems"
						:currentPage="page"
					/>
				</template>
				<template slot="actions">
					<j-button
						v-can:and="['writeLogins', 'writeInternals']"
						:to="`${$route.path}/create-user`"
					>
						<crm-icon name="add" size="small" color="c5" />
						{{ $t("create_user") }}
					</j-button>
				</template>
			</core-layout-headline-and-actions>

			<!-- Data Container -->
			<j-card class="bg5">
				<j-data-table
					:header="header"
					:list="computedList"
					:sortable="false"
					:isLoading="isLoading"
					@sort="onSort($event)"
				>
					<template slot="list" slot-scope="{ item }">
						<td v-for="(column, i) in header" :key="i">
							<template>
								<!-- Actions -->
								<template v-if="column.field === 'actions'">
									<div class="flex wrap xs-middle">
										<j-button
											v-if="!item.isEmailConfirmed"
											type="tertiary"
											size="small"
											class="mr2 mb2"
											@click="reinviteUser(item.id)"
										>{{ $t("reinvite") }}</j-button>
										<j-button
											v-if="item.isLockedOut"
											v-can:and="['writeLogins', 'writeInternals']"
											type="tertiary"
											size="small"
											class="mb2"
											:isLoading="unlockingLoginId === item.id"
											@click="
												unlockLogin(item.id, item.lockoutEndDateUtc)
											"
										>{{ $t("unlock_user") }}</j-button>
									</div>
								</template>

								<!-- Name -->
								<template v-else-if="column.field === 'name' && item.associatedUser">
									<router-link
										:to="{
											name: 'UsersUpdate',
											params: { loginId: item.id }
										}"
									>{{ item.name }}</router-link>
								</template>

								<!-- Others -->
								<template v-else>{{ item[column.field] }}</template>
							</template>
						</td>
					</template>
					<template slot="empty">{{ $t("errors.no_data") }}</template>
				</j-data-table>
			</j-card>

			<!-- Pagination -->
			<div class="flex xs-12 p4 xs-center mb15">
				<j-pagination
					:amount="listing.numberOfItems"
					:perPage="itemsPerPage"
					:activePage="page"
					@click="onPageChange($event)"
				/>
			</div>
		</core-layout-content>

		<!-- Router view -->
		<router-view />
	</div>
</template>

<script>
import CrmIcon from "@/components/CrmIcon/"
import JButton from "@/components/JButton/"
import JCard from "@/components/JCard/"
import JDataTable from "@/components/JDataTable/"
import JSearch from "@/components/JSearch/"
import JPagination from "@/components/JPagination/"
import CorePaginationInfo from "@/modules/core/components/CorePaginationInfo"
import JBaseInputSearch from "@/components/JBaseInputSearch/"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import UsersSubmenu from "../components/UsersSubmenu"
import { mapState } from "vuex"
import dayjs from "dayjs"
import api from "@/modules/users/api/"
import get from "lodash.get"
import crmConfig from "@/config/"

export default {
	components: {
		CrmIcon,
		JButton,
		JCard,
		JDataTable,
		JSearch,
		JPagination,
		CorePaginationInfo,
		JBaseInputSearch,
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions,
		UsersSubmenu
	},

	data() {
		return {
			isLoading: false,

			// Actual fetched data
			listing: {
				numberOfItems: 0,
				list: []
			},

			// Pagination
			page: 1,
			itemsPerPage: 30,

			// When inviting user through modal,
			// here we store info about the user that should be invited
			userToBeInvited: null,

			unlockingLoginId: null,

			// Header of table
			header: [
				{
					field: "name",
					text: this.$t("name")
				},
				{
					field: "email",
					text: this.$t("email_or_username")
				},
				{
					text: this.$t("code"),
					field: "internalCode"
				},
				{
					text: this.$t("permission_groups"),
					field: "permissionGroups"
				},
				{
					text: this.$t("status"),
					field: "status"
				},
				{
					text: this.$t("actions"),
					field: "actions"
				},
				{
					text: this.$t("created"),
					field: "createdAt"
				}
			]
		}
	},

	computed: {
		...mapState(["settings"]),
		computedList() {
			return this.listing.list.map(item => {
				const isLockedOut = this.isLoginLockoutActive(item.lockoutEndDateUtc)
				return {
					id: item.id, // id of login
					name: get(item, "associatedUser.name")
						? get(item, "associatedUser.name")
						: "---",
					email: item.email,
					internalCode: get(item, "associatedUser.internalCode"),
					isEmailConfirmed: item.isEmailConfirmed,
					associatedUser: item.associatedUser,
					permissionGroups: item.permissionGroups
						? item.permissionGroups.map(pg => pg.name).join(",")
						: null,
					lockoutEndDateUtc: item.lockoutEndDateUtc,
					isLockedOut,
					status: isLockedOut
						? this.$t("locked_out")
						: item.isEmailConfirmed
							? this.$t("active")
							: this.$t("pending_invitation"),
					createdAt: item.createdAt
						? dayjs(item.createdAt).format(this.settings.dateTimeFormat)
						: null
				}
			})
		}
	},

	methods: {
		isLoginLockoutActive(lockoutEndDateUtc) {
			if (lockoutEndDateUtc == null || lockoutEndDateUtc === "") return false
			const endMs = new Date(lockoutEndDateUtc).getTime()
			if (Number.isNaN(endMs)) return false
			return Date.now() < endMs
		},

		unlockDateTimeUtcBeforeLockout(lockoutEndDateUtc) {
			const endMs = new Date(lockoutEndDateUtc).getTime()
			if (Number.isNaN(endMs))
				return new Date(Date.now() - 1000).toISOString().replace(/Z$/, "+00:00")
			const unlockMs = Math.min(endMs - 1, Date.now() - 1000)
			return new Date(unlockMs).toISOString().replace(/Z$/, "+00:00")
		},

		onPageChange(pageIndex) {
			this.page = pageIndex
			this.readAll()
			this.scrollToTop()
		},

		scrollToTop() {
			this.$scrollTo("body", 300)
		},

		async readAll() {
			let sort = { fieldName: "createdAt", type: "desc" }

			// Pagination
			const skip = (this.page - 1) * this.itemsPerPage
			const limit = this.itemsPerPage

			this.isLoading = true
			const res = await api.readAllLogins({ sort, skip, limit })
			this.isLoading = false

			this.listing.list = res.data.logins.list
			this.listing.numberOfItems = res.data.logins.totalCount
		},

		async reinviteUser(loginId) {
			const clientId = crmConfig.CLIENT_ID
			const res = await api.resendConfirmationEmail({
				clientId,
				loginId
			})

			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}

			this.$store.dispatch("addMessage", {
				message: {
					type: "success",
					text: this.$t("user_reinvited")
				}
			})
		},

		async unlockLogin(loginId, lockoutEndDateUtc) {
			this.unlockingLoginId = loginId
			try {
				const dateTime = this.unlockDateTimeUtcBeforeLockout(lockoutEndDateUtc)
				const res = await api.unlockLogin({ loginId, dateTime })

				if (res.errors) {
					this.$store.dispatch("showError", res.errors)
					return
				}

				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("user_unlocked")
					}
				})
				await this.readAll()
			} catch (error) {
				this.$store.dispatch("showError", [error.message || this.$t("errors.general")])
			} finally {
				this.unlockingLoginId = null
			}
		}
	},

	mounted() {
		this.readAll()
	}
}
</script>
